import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
  Skeleton,
  TabPanels,
  useCopyToClipboard,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { useResource } from '../lib/hooks';
import { prettyJson } from '../lib/format';

interface OpenApiDocument {
  info: { title: string; version: string; description: string };
  servers: Array<{ url: string }>;
  tags: Array<{ name: string }>;
  paths: Record<string, Record<string, { tags: string[]; summary: string; description: string }>>;
}

interface RouteInventory {
  gateway: Array<{ method: string; url: string }>;
  management: number;
}

/**
 * In-panel API reference.
 *
 * Renders the generated management document and the data-plane route inventory
 * without loading anything from an external CDN, so it works behind a strict
 * content security policy and offline.
 */
export function ApiReferencePage() {
  const document = useResource<OpenApiDocument>('/api/admin/openapi.json');
  const inventory = useResource<RouteInventory>('/api/admin/route-inventory');
  const [tag, setTag] = useState('');
  const [copied, copy] = useCopyToClipboard();

  if (document.loading) return <Skeleton rows={8} />;
  if (!document.data)
    return <Callout tone="danger">Nie udało się wczytać dokumentu OpenAPI.</Callout>;

  const rows = Object.entries(document.data.paths).flatMap(([path, methods]) =>
    Object.entries(methods).map(([method, operation]) => ({
      key: `${method} ${path}`,
      method: method.toUpperCase(),
      path,
      tag: operation.tags[0] ?? 'other',
      summary: operation.summary,
      description: operation.description,
    })),
  );
  const filtered = tag ? rows.filter((row) => row.tag === tag) : rows;

  return (
    <>
      <PageHeader
        title="Dokumentacja API"
        description="Referencja generowana z rzeczywistej tablicy tras — nie może rozejść się z kodem."
        actions={
          <Button onClick={() => copy(JSON.stringify(document.data, null, 2))}>
            {copied ? 'Skopiowano' : 'Kopiuj OpenAPI (JSON)'}
          </Button>
        }
      />

      <TabPanels
        tabs={[
          {
            value: 'management',
            label: `Management API (${rows.length})`,
            content: (
              <Card tight>
                <div className="ah-card-body">
                  <Callout>{document.data.info.description}</Callout>
                  <div className="ah-toolbar">
                    <Field label="Obszar">
                      {(id) => (
                        <Select
                          id={id}
                          value={tag}
                          onChange={(event) => setTag(event.target.value)}
                        >
                          <option value="">Wszystkie</option>
                          {document.data!.tags.map((entry) => (
                            <option key={entry.name} value={entry.name}>
                              {entry.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                </div>
                <DataTable
                  rows={filtered}
                  rowKey={(row) => row.key}
                  empty={<EmptyState title="Brak endpointów" />}
                  columns={[
                    {
                      key: 'method',
                      header: 'Metoda',
                      render: (row) => (
                        <Badge
                          tone={
                            row.method === 'GET'
                              ? 'ok'
                              : row.method === 'DELETE'
                                ? 'danger'
                                : 'warn'
                          }
                        >
                          {row.method}
                        </Badge>
                      ),
                    },
                    {
                      key: 'path',
                      header: 'Ścieżka',
                      render: (row) => <span className="ah-mono">{row.path}</span>,
                    },
                    { key: 'tag', header: 'Obszar', render: (row) => row.tag },
                    {
                      key: 'summary',
                      header: 'Opis',
                      render: (row) => (
                        <>
                          <div>{row.summary}</div>
                          <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                            {row.description}
                          </div>
                        </>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            value: 'gateway',
            label: 'Data plane',
            content: (
              <Card
                title="Punkty końcowe bramy"
                description="Uwierzytelniane kluczem Hub. Pełny, przefiltrowany dokument OpenAPI pobierze klient przez GET /api/v1/openapi.json."
                tight
              >
                <DataTable
                  rows={inventory.data?.gateway ?? []}
                  rowKey={(row) => `${row.method} ${row.url}`}
                  empty={<EmptyState title="Brak tras" />}
                  columns={[
                    {
                      key: 'method',
                      header: 'Metoda',
                      render: (row) => (
                        <Badge tone={row.method === 'GET' ? 'ok' : 'warn'}>{row.method}</Badge>
                      ),
                    },
                    {
                      key: 'url',
                      header: 'Ścieżka',
                      render: (row) => <span className="ah-mono">{row.url}</span>,
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            value: 'raw',
            label: 'Surowy dokument',
            content: (
              <Card>
                <pre className="ah-code">{prettyJson(document.data)}</pre>
              </Card>
            ),
          },
        ]}
      />
    </>
  );
}
